import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthStore } from '@/core/auth/stores/auth.store';
import { AUTH_REFRESH } from '@/core/auth/tokens/auth-session.token';
import { IS_SESSION_REFRESH } from '@/core/auth/session-refresh-context';
import { EnvFeatureService } from '@/core/env/env.service';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  // Only attach the Dialab session (Bearer + credentials) to our own API. apiBaseInterceptor runs
  // first and rewrites API calls to absolute apiBaseUrl URLs, so anything not under apiBaseUrl
  // (i18n assets, Strapi, other origins) must not receive the token.
  const apiBaseUrl = inject(EnvFeatureService).apiBaseUrl;
  if (!apiBaseUrl || !request.url.startsWith(apiBaseUrl)) {
    return next(request);
  }

  const store = inject(AuthStore);
  const refresh = inject(AUTH_REFRESH, { optional: true });
  const authedRequest = withCredentials(request, store.accessToken());

  return next(authedRequest).pipe(
    catchError((error: unknown) => {
      if (
        !(error instanceof HttpErrorResponse) ||
        error.status !== 401 ||
        isSsoUrl(request.url) ||
        request.context.get(IS_SESSION_REFRESH) ||
        !refresh
      ) {
        return throwError(() => error);
      }

      // The refresh runs the whole chain again, so its own failure arrives already mapped: a 401
      // there means the session is gone and errorInterceptor has signed out; anything else is the
      // API being unwell, and must not cost a session that a retry could still renew.
      return refresh().pipe(switchMap(() => next(withCredentials(request, store.accessToken()))));
    }),
  );
};

function withCredentials(
  request: HttpRequest<unknown>,
  token: string | null,
): HttpRequest<unknown> {
  return request.clone({
    withCredentials: true,
    ...(token ? { setHeaders: { Authorization: `Bearer ${token}` } } : {}),
  });
}

function isSsoUrl(url: string): boolean {
  try {
    return new URL(url).pathname.startsWith('/sso/');
  } catch {
    return url.includes('/sso/');
  }
}
